// Relevé automatique : le dossier de visite (pièces cotées sur les plans,
// travaux proposés, photos situées) devient les pièces et les lignes de la
// page devis. Rien à ressaisir ; tout reste modifiable.
//
// Module pur, sans import : la page injecte ses tables et ses outils, et un
// script Node peut le rejouer sur un dossier réel (scripts/test-releve-dossier).
//
// Les mêmes règles que le chiffrage serveur (chantiers/devis.chiffrage.js),
// lues dans la même table (src/data/bases.json) :
// - une ligne qui nomme des pièces cotées va dans ces pièces ; surfacique,
//   c'est la géométrie de la pièce qui donne la quantité (qty 1 = auto) ;
// - une ligne sans pièce mais dont le lieu se lit (« Dalle béton cave »,
//   photos de cave citées) va dans les pièces de ce lieu s'il est coté, sinon
//   dans une pièce à part portant ce nom (Cave, Façade / extérieur…) ;
// - une ligne surfacique sans rien (« Peinture murs », zones vide) vaut pour
//   toutes les pièces cotées, comme le dit la consigne d'analyse ;
// - tout le reste (équipements, réseaux, installation de chantier) va dans
//   « Ensemble du bien », jamais répété par pièce. C'est cette répétition qui
//   faisait 573 000 € d'un chantier de 100 000.

const GEOMETRIQUES = ['murs', 'plafond', 'sol', 'perimetre'];
const nomNorm = (x) => String(x || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const TYPE_PAR_NOM = [
  [/s[ée]jour|salon|living|pi[èe]ce de vie|salle [àa] manger/i, 'sejour'], [/cuisine/i, 'cuisine'], [/chambre/i, 'chambre'],
  [/salle de bain|sdb|salle d'eau|douche/i, 'sdb'], [/\bwc\b|toilette/i, 'wc'], [/couloir|entr[ée]e|d[ée]gagement|palier/i, 'couloir'],
  [/fa[çc]ade|ext[ée]rieur|balcon|terrasse/i, 'facade'],
];
const typeDe = (nom) => (TYPE_PAR_NOM.find(([re]) => re.test(nom || '')) || [null, 'autre'])[1];

export const ENSEMBLE = 'Ensemble du bien';

/** La catégorie de lieu (Cave, Garage, Cuisine…) que nomme un texte, selon la table partagée ; null sinon. */
export function categorieLieu(bases, texte) {
  const LIEUX = bases.lieux.map(([motif, nom]) => [new RegExp(motif, 'i'), nom]);
  return (LIEUX.find(([re]) => re.test(texte || '')) || [null, null])[1];
}

// Le niveau qu'un nom de pièce porte (« RDC - Chambre 1 », « Cuisine (1er
// étage) », « 2e étage - Chambre 4 ») — ou null s'il n'en dit rien.
const NIVEAUX = [
  [/sous-sol|\bcave\b|\bcaves\b|local enterr/i, 'Sous-sol'],
  [/\brdc\b|rez[- ]de[- ]ch/i, 'Rez-de-chaussée'],
  [/\b1\s*(er|re)?\s*[ée]tage|premier [ée]tage/i, '1er étage'],
  [/\b2\s*(e|ème|eme)?\s*[ée]tage|deuxi[èe]me [ée]tage/i, '2e étage'],
  [/\b3\s*(e|ème|eme)?\s*[ée]tage|troisi[èe]me [ée]tage/i, '3e étage'],
  [/comble|grenier|toiture/i, 'Combles'],
  [/fa[çc]ade|ext[ée]rieur|jardin|terrasse|balcon/i, 'Extérieur'],
];
export function niveauDuNom(nom) {
  return (NIVEAUX.find(([re]) => re.test(nom || '')) || [null, null])[1];
}
export const ORDRE_NIVEAUX = ['Sous-sol', 'Rez-de-chaussée', '1er étage', '2e étage', '3e étage', 'Combles', 'Extérieur'];

/**
 * Part de l'état du bien qui s'applique à un poste (miroir de
 * chantiers/bases.js) : les supports en entier, les réseaux à moitié, les
 * équipements à peine. Cf. bases.json → sensibiliteEtat.
 */
export function sensibiliteEtat(bases, cle, unit) {
  const S = bases.sensibiliteEtat;
  if (S.parPoste[cle] != null) return S.parPoste[cle];
  const cat = String(cle || '').split('.')[0];
  const surface = ['m²', 'm2', 'ml'].includes(unit);
  // Un forfait par logement ou par niveau (lots techniques, cf. reseaux.js)
  // n'est généré que si le réseau est à refaire : son prix est celui de la
  // grille, l'état du bien le suit en entier.
  if (unit === 'logement' || unit === 'niveau') return S.supports;
  if (S.categoriesReseaux.includes(cat)) return unit === 'u' ? S.equipements : S.reseaux;
  return surface ? S.supports : S.equipements;
}

/** Position effective dans la fourchette : le niveau « standard », tiré vers l'état du bien selon la sensibilité du poste. */
export function positionSelonEtat(bases, cle, unit, posEtat) {
  const standard = bases.sensibiliteEtat.standard ?? 0.33;
  return standard + (posEtat - standard) * sensibiliteEtat(bases, cle, unit);
}

// L'état du bien ne fait plus courir un poste de son mini à son maxi (×2 sur
// la peinture) : il le retient d'au plus 5 % sous son prix maxi. Relecture du
// devis Martin avec Sorin et Marius, 12/10/2026 — « indiferent ce vopsea faci,
// nu există preț sub 22 € pe m² » : le prix de la grille EST le prix, l'état
// n'est qu'une nuance. Un poste a donc UN prix dans tout le devis.
export const AMPLITUDE_ETAT = 0.05;

/** Prix unitaire d'un poste : son maxi, retenu de l'amplitude selon la position (0 = neuf, 1 = vétuste). Miroir de chantiers/bases.js. */
export function prixSelonEtat(item, position) {
  if (!item) return 0;
  const max = Number(item.priceMax ?? item.price ?? 0);
  const p = Math.min(1, Math.max(0, Number(position) || 0));
  return max * (1 - AMPLITUDE_ETAT * (1 - p));
}
export const NOTE_PROVISOIRE = 'Dimensions provisoires (3 × 3 m) — à relever';

// ── Les photos trouvent leur pièce ─────────────────────────────────────────
// Une cote saisie sans nom (« Pièce 1 ») ou avec un mot qui n'est pas une
// pièce (« Diminuer ») est une pièce au nom générique : les photos peuvent la
// nommer — une pièce = ses photos, une photo = une pièce (27/09/2026).
const MOTS_GENERIQUES = new Set(['piece', 'pieces', 'etage', 'etages', 'niveau', 'photo', 'photos', 'espace', 'autre', 'autres', 'meme', 'memes', 'partie', 'differ']);
/** Jetons distinctifs d'un texte : mots de cinq lettres et plus, sans accents, ramenés à six lettres. */
export function jetons(texte) {
  return new Set(nomNorm(texte).split(/[^a-z]+/).filter(m => m.length >= 5).map(m => m.slice(0, 6)));
}
/** Un nom de pièce qui ne dit ni un lieu (cave, cuisine…) ni un type (chambre, séjour…). */
export function estNomGenerique(bases, nom) {
  if (!nom || nom === ENSEMBLE) return false;
  // Générique = un nom qui ne dit rien (« Pièce 2 », « Zone », « Espace 3 »).
  // Un nom saisi qui n'est simplement pas dans le dictionnaire des lieux
  // (« Cabinet 1 », « Bureau d'explication ») n'en est pas un : le 13/10/2026,
  // l'extension de Châteauroux a vu « Cabinet 1 » renommé « Séjour » par ses photos.
  return /^(pi[eè]ce|zone|espace|local|salle)(\s*\d+)?$/i.test(String(nom).trim()) && !categorieLieu(bases, nom);
}
/** Le lieu d'une photo, propre : « Pièce mansardée de l'étage supérieur (même espace vue différente) » → « Pièce mansardée de l'étage supérieur ». */
const lieuPropre = (lieu) => {
  const t = String(lieu || '').replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};

/**
 * Range chaque photo dans UNE pièce, en cinq tours : 0 la pièce désignée par
 * l'arbitre ; 1 la catégorie du lieu (+ étage) ; 2 la mention du nom ; 3 le
 * vocabulaire des notes de la pièce (« pièce mansardée de l'étage supérieur »
 * rejoint la pièce dont les travaux parlent de la pièce mansardée de l'étage) ;
 * 4 une pièce au nom générique, par groupe de photos du même lieu. Puis les
 * pièces génériques qui ont reçu des photos aux tours 3 et 4 prennent le nom
 * de ce que montrent leurs photos.
 * @param {Array<{id, caption}>} photos
 * @param {Array} obs  observations (nom, lieu, piece, lieuInspection)
 * @param {Array<{nom, niveau, texte, nbPhotos, generique}>} pieces
 * @param {Function} lieuDuTexte  texte → catégorie de lieu ou null
 * @returns {{ par: Map<string,string>, renommages: Map<string,string> }} par : id photo → nom de pièce
 */
export function placerPhotos(photos, obs, pieces, lieuDuTexte) {
  const obsDe = (p) => obs.find(x => x.nom === p.caption);
  // Une photo de plan sert au relevé des cotes, pas à montrer une pièce.
  const estPlan = (p) => !!obsDe(p)?.plan;
  const compte = new Map(pieces.map(pc => [pc.nom, pc.nbPhotos || 0]));
  const par = new Map(), tourDe = new Map();
  const poser = (nom, p, tour) => { par.set(p.id, nom); tourDe.set(p.id, tour); compte.set(nom, (compte.get(nom) || 0) + 1); };
  const nivOk = (pc, niv) => !niv || !pc.niveau || pc.niveau === niv;
  for (const p of photos) {   // tour 0
    const o = obsDe(p);
    if (o?.piece && compte.has(o.piece)) poser(o.piece, p, 0);
  }
  for (const p of photos) {   // tour 1
    if (par.has(p.id) || estPlan(p)) continue;
    const o = obsDe(p); const cat = lieuDuTexte(o?.lieu);
    if (!cat) continue;
    const niv = niveauDuNom(o?.lieu || '');
    const cible = pieces.find(pc => lieuDuTexte(pc.nom) === cat && nivOk(pc, niv)) || pieces.find(pc => lieuDuTexte(pc.nom) === cat);
    if (cible) poser(cible.nom, p, 1);
  }
  for (const p of photos) {   // tour 2
    if (par.has(p.id) || estPlan(p)) continue;
    const o = obsDe(p); const l = nomNorm(o?.lieu);
    if (!l) continue;
    const niv = niveauDuNom(o?.lieu || '');
    const cible = pieces.find(pc => nomNorm(pc.nom).length >= 3 && (nomNorm(pc.nom).includes(l) || l.includes(nomNorm(pc.nom))) && nivOk(pc, niv));
    if (cible) poser(cible.nom, p, 2);
  }
  for (const p of photos) {   // tour 3
    if (par.has(p.id) || estPlan(p)) continue;
    const o = obsDe(p);
    const distinct = [...jetons([o?.lieu, o?.lieuInspection].filter(Boolean).join(' '))].filter(j => !MOTS_GENERIQUES.has(j));
    if (!distinct.length) continue;
    let best = null, bestScore = 0;
    for (const pc of pieces) {
      const jr = jetons(pc.texte || '');
      const score = distinct.filter(j => jr.has(j)).length;
      if (score > bestScore) { best = pc; bestScore = score; }
    }
    if (best) poser(best.nom, p, 3);
  }
  // tour 4 : par groupe de photos du même lieu, la pièce générique la moins pourvue
  const groupes = new Map();
  for (const p of photos) {
    if (par.has(p.id) || estPlan(p)) continue;
    const o = obsDe(p); if (!o?.lieu) continue;
    const cle = nomNorm(lieuPropre(o.lieu));
    (groupes.get(cle) || groupes.set(cle, []).get(cle)).push(p);
  }
  for (const [, groupe] of groupes) {
    const niv = niveauDuNom(obsDe(groupe[0])?.lieu || '');
    const cands = pieces.filter(pc => pc.generique && nivOk(pc, niv)).sort((a, b) => (compte.get(a.nom) || 0) - (compte.get(b.nom) || 0));
    if (!cands.length) continue;
    for (const p of groupe) poser(cands[0].nom, p, 4);
  }
  // Les pièces génériques nommées par leurs photos (tours 3 et 4).
  const renommages = new Map();
  const pris = new Set(pieces.map(pc => nomNorm(pc.nom)));
  for (const pc of pieces) {
    if (!pc.generique) continue;
    const lieux = photos.filter(p => par.get(p.id) === pc.nom && tourDe.get(p.id) >= 3).map(p => obsDe(p)?.lieu).filter(Boolean);
    if (!lieux.length) continue;
    const votes = new Map();
    for (const l of lieux) { const n = lieuDuTexte(l) || lieuPropre(l); votes.set(n, (votes.get(n) || 0) + 1); }
    let nom = [...votes].sort((a, b) => b[1] - a[1])[0][0];
    if (!nom || nomNorm(nom) === nomNorm(pc.nom)) continue;
    if (pris.has(nomNorm(nom))) { let k = 2; while (pris.has(nomNorm(`${nom} ${k}`))) k++; nom = `${nom} ${k}`; }
    pris.add(nomNorm(nom)); pris.delete(nomNorm(pc.nom));
    renommages.set(pc.nom, nom);
  }
  return { par, renommages };
}

/**
 * @param {object} data   le lead, avec son `dossier`
 * @param {object} o
 * @param {Set<string>} o.connues   clés du référentiel (une clé inconnue n'aurait pas de prix)
 * @param {object} o.bases          src/data/bases.json
 * @param {Array}  o.roomTypes      types de pièce de la page ({ id, label })
 * @param {number} o.wallHeight     hauteur par défaut
 * @param {Function} o.rectVertices (longueur, largeur) → sommets
 * @param {object} o.stateMultiplier états connus de la page
 * @returns {{ rooms, projectPhotos, etat, tvaCle, sansCotes } | null} null si rien ne permet de construire une pièce
 */
export function construireDepuisDossier(data, { connues, bases, roomTypes, wallHeight, rectVertices, stateMultiplier }) {
  const dossier = data?.dossier || {};
  const dz = dossier.releve || {};
  const obs = dossier.observationsPhotos || [];
  const chiffre = dossier.chiffrage?.base || [];   // aligné sur dz.travaux : quantités du moteur serveur
  const LIEUX = bases.lieux.map(([motif, nom]) => [new RegExp(motif, 'i'), nom]);
  const lieuDuTexte = (texte) => (LIEUX.find(([re]) => re.test(texte || '')) || [null, null])[1];
  const lieuDe = (t) => t.lieu || lieuDuTexte([t.designation, t.note].filter(Boolean).join(' '));
  const regexDuLieu = (lieu) => LIEUX.find(([, nom]) => nom === lieu)?.[0];
  const baseDe = (t, unit) => {
    if (unit && !['m²', 'm2', 'ml'].includes(unit)) return 'quantite';   // une unité ne se développe jamais sur la géométrie
    const id = String(t.item || '').split('.')[1] || '';
    if ((bases.seQuantifient || []).includes(id)) return 'quantite';   // une cloison n'est pas les murs d'une pièce
    return GEOMETRIQUES.find(b => bases[b].includes(id)) || (GEOMETRIQUES.includes(t.base) ? t.base : 'quantite');
  };

  // Pièces cotées d'abord ; sinon les lieux reconnus sur les photos, sans dimensions.
  const sansCotes = !dz.zones?.length;
  const lieuxPhotos = [...new Set(obs.filter(o => !o.plan).map(o => lieuDuTexte(o.lieu)).filter(Boolean))];
  const zones = sansCotes ? lieuxPhotos.map(nom => ({ nom })) : dz.zones;
  if (!zones.length) return null;

  const lignes = new Map(zones.map(z => [z.nom, []]));
  const aPart = new Map();      // pièces créées pour un lieu non coté, ou l'ensemble du bien
  const notes = new Map();
  // Une ligne garde ce qui aide le métreur à la valider : la désignation et la
  // note de l'analyse, et si la quantité ou le poste restent à confirmer.
  // Plus de difficulté (12/10/2026) : le prix ne dépend que du poste et de l'état.
  const ligne = (t, qty) => ({
    key: t.item, qty, priceOverride: null,
    ...(t.note ? { note: t.note } : {}),
    ...(t.confiance === 'a_confirmer' || t.quantiteAConfirmer ? { aConfirmer: true } : {}),
  });
  const poser = (nom, t, qty) => {
    lignes.get(nom).push(ligne(t, qty));
    if (t.note) (notes.get(nom) || notes.set(nom, []).get(nom)).push(t.note);
  };
  const aPartGeo = new Set();   // lieux non cotés qui portent au moins un poste au m² ou au ml
  const poserAPart = (nom, t, qty, geo = false) => { (aPart.get(nom) || aPart.set(nom, []).get(nom)).push(ligne(t, qty)); if (geo) aPartGeo.add(nom); };
  // Une quantité sur plusieurs pièces : partagée, le reste aux premières.
  const repartir = (noms, t) => {
    const q = t.quantite ?? noms.length;
    noms.forEach((nom, k) => {
      const part = Math.floor(q / noms.length) + (k < q % noms.length ? 1 : 0);
      if (part > 0) poser(nom, t, part);
    });
  };

  (dz.travaux || []).forEach((t, i) => {
    if (!connues.has(t.item)) return;
    // Un « conservé » mis en ligne par le modèle (quantité 0) n est pas une ligne.
    // Attention : Number(null) vaut 0 — une surface sans quantité (null) se développe.
    if (t.quantite != null && t.quantite !== "" && Number(t.quantite) === 0) return;
    const geometrique = baseDe(t, chiffre[i]?.unite) !== 'quantite';
    const nommees = (t.zones || []).filter(n => lignes.has(n));
    if (nommees.length) {
      if (geometrique) nommees.forEach(n => poser(n, t, 1));
      else repartir(nommees, t);
      return;
    }
    const lieu = lieuDe(t);
    const auLieu = lieu ? zones.filter(z => regexDuLieu(lieu).test(z.nom)).map(z => z.nom) : [];
    if (auLieu.length) {
      if (geometrique && t.quantite == null) auLieu.forEach(n => poser(n, t, 1));
      else poser(auLieu[0], t, t.quantite ?? 1);
      return;
    }
    if (geometrique && t.quantite == null && !lieu && !sansCotes) {
      zones.forEach(z => poser(z.nom, t, 1));   // « zones vide = toutes les pièces »
      return;
    }
    poserAPart(lieu || ENSEMBLE, t, t.quantite ?? chiffre[i]?.quantite ?? 1, geometrique);
  });
  // Un lieu non coté qui ne porte que des équipements (interphone, compteur…)
  // n'est pas une pièce : ses lignes rejoignent l'ensemble du bien, sans 3 × 3.
  for (const [nom, ls] of [...aPart]) {
    if (nom === ENSEMBLE || aPartGeo.has(nom)) continue;
    (aPart.get(ENSEMBLE) || aPart.set(ENSEMBLE, []).get(ENSEMBLE)).push(...ls);
    aPart.delete(nom);
  }

  // Les photos de la visite se rangent dans leur pièce ; le reste va au projet.
  const photosDossier = (dossier.photos || []).map(p => ({ id: p.id, url: `/api/photo/${p.id}`, src: `/api/photo/${p.id}`, caption: p.nom || '' }));
  const obsDe = (p) => obs.find(x => x.nom === p.caption);
  // Une photo de plan sert au relevé des cotes, pas à montrer une pièce.
  const estPlan = (p) => !!obsDe(p)?.plan;
  // Une photo va à UNE pièce, en deux tours. D'abord par catégorie : le
  // premier lieu que nomme l'observation (« Cave / sous-sol (garage ou
  // débarras) » → Cave) rejoint la pièce de cette catégorie, à l'étage écrit
  // s'il l'est. Ensuite seulement, ce qui reste se place par simple mention
  // du nom. Sans ce premier tour, une photo de cave partait dans le débarras
  // du 1er parce que « débarras » figurait dans la phrase.
  const niveauDePiece = (nom) => { const z = zones.find(x => x.nom === nom); return (z?.niveau && (niveauDuNom(z.niveau) || z.niveau)) || niveauDuNom(nom) || null; };
  // Ce que la pièce dit d'elle-même : son nom, les notes et les libellés des travaux qu'elle reçoit.
  const texteDePiece = (nom) => [nom, ...(notes.get(nom) || []), ...(dz.travaux || []).filter(t => (t.zones || []).includes(nom)).map(t => t.designation)].filter(Boolean).join(' ');
  const piecesPourPhotos = () => [...zones.map(z => z.nom), ...[...aPart.keys()]].map(nom => ({
    nom, niveau: niveauDePiece(nom), texte: texteDePiece(nom), nbPhotos: 0,
    generique: !sansCotes && !aPart.has(nom) && estNomGenerique(bases, nom),
  }));
  const { par, renommages } = placerPhotos(photosDossier.filter(p => !estPlan(p)), obs, piecesPourPhotos(), lieuDuTexte);
  // Une pièce ne prend jamais le nom d'un plan.
  for (const [k, v] of [...renommages]) if (/^plan\b/i.test(v)) renommages.delete(k);
  // tour 5 : une photo d'un lieu reconnu (cuisine, cave…) sans aucune pièce de ce lieu
  // ouvre une pièce provisoire à ce nom — comme lorsqu'aucune cote n'a été saisie.
  for (const p of photosDossier) {
    if (par.has(p.id) || estPlan(p)) continue;
    const cat = lieuDuTexte(obsDe(p)?.lieu);
    if (!cat || zones.some(z => lieuDuTexte(z.nom) === cat)) continue;
    if (!aPart.has(cat)) aPart.set(cat, []);
    par.set(p.id, cat);
  }
  const placees = new Set(par.keys());
  const photosPour = (nom) => photosDossier.filter(p => par.get(p.id) === nom);

  // Une pièce relevée sur un plan photographié sait de quel niveau elle est
  // (« plan (photo 2) » → l'observation de la photo 2 porte le niveau écrit
  // sur le croquis) et quelle photo la montre. Sinon, son nom le dit — ou rien.
  const photoParN = Object.fromEntries((dossier.photos || []).map((p, k) => [k + 1, p]));
  const planDe = (z) => {
    const m = /plan \(photo (\d+)\)/i.exec(z?.source || '');
    if (!m) return null;
    const o = obs.find(x => x.n === Number(m[1]));
    return { niveau: niveauDuNom(o?.plan?.niveau || o?.lieu || '') || (o?.plan?.niveau || null), photoId: photoParN[Number(m[1])]?.id || null };
  };
  let n = 0;
  const piece = (nom, z, lines, note) => {
    // Une pièce au nom générique prend le nom que ses photos lui donnent.
    const nomFinal = renommages.get(nom) || nom;
    const type = typeDe(nomFinal);
    const plan = planDe(z);
    const niveau = (z?.niveau && (niveauDuNom(z.niveau) || z.niveau)) || niveauDuNom(nomFinal) || plan?.niveau || null;
    return {
      id: Date.now() + (n++), type, label: roomTypes.find(rt => rt.id === type)?.label || nomFinal, customLabel: nomFinal,
      // L'ensemble du bien n'a pas de contour : ses surfaces sont la somme des pièces (page).
      vertices: nom === ENSEMBLE ? [] : rectVertices(Number(z?.longueur) || 3, Number(z?.largeur) || 3), walls: [],
      height: String(z?.hauteur || wallHeight), lines, addons: [], photos: photosPour(nom),
      ...(z?.mansarde ? { mansarde: Number(z.mansarde) } : {}),
      notes: [note, nomFinal !== nom ? `Nommée d'après ses photos (cote saisie « ${nom} »)` : ''].filter(Boolean).join(' · '),
      // Dimensions non relevées : la page le signale et demande de les assumer avant l'envoi.
      provisoire: nom !== ENSEMBLE && /provisoire/i.test(note || ''),
      // L'étage posé par l'analyse (cote saisie ou plan relu) d'abord, sinon ce que dit le nom.
      niveau,
      planPhotoId: plan?.photoId || (z?.plan ? planDe({ source: z.plan })?.photoId : null) || null,
    };
  };
  const rooms = [
    ...zones.map(z => piece(z.nom, z, lignes.get(z.nom), (sansCotes || !z.longueur) ? NOTE_PROVISOIRE : (z.supposee ? `Surface supposée : ${z.supposee}` : ''))),
    ...[...aPart].map(([nom, lines]) => piece(nom, null, lines,
      nom === ENSEMBLE ? 'Postes du chantier entier (équipements, réseaux, installation) — sans pièce' : `Lieu non coté sur le relevé — ${NOTE_PROVISOIRE.toLowerCase()}`)),
  ];

  return {
    rooms,
    projectPhotos: photosDossier.filter(p => !placees.has(p.id) && !estPlan(p)),   // un plan n est ni une pièce ni une couverture
    etat: dz.etat && stateMultiplier[dz.etat] ? dz.etat : null,
    tvaCle: { 0.055: 'tva_55', 0.1: 'tva_10', 0.2: 'tva_20' }[Number(dz.tva)] || null,
    sansCotes,
  };
}

/**
 * Empreinte des pièces telles que construites : si elle n'a pas changé, le
 * relevé n'a jamais été retouché et peut suivre une nouvelle analyse du
 * dossier ; sinon on le garde et on propose.
 */
export function empreinteDe(rooms) {
  const s = JSON.stringify((rooms || []).map(r => [
    r.customLabel || r.label, r.height, r.vertices,
    (r.lines || []).map(l => [l.key, l.qty, l.priceOverride, l.difficulty]),
    (r.addons || []).length, (r.photos || []).length,
  ]));
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return String(h);
}
